/**
 * The full 157-report manifest set per M3X.REPORTS §1.A-§1.D.
 *
 * Imported by `catalog.ts` so the catalog exports a single
 * REPORTS_CATALOG array. Per-report rendering uses the
 * `<ConfigurableReportPage />` template with the per-report
 * `<reportConfigForManifest()>` builder.
 *
 * Layout:
 *   §1.A.1  — Leaves (15)
 *   §1.A.2  — Payroll (20)
 *   §1.A.3a — Loans (10)
 *   §1.A.3b — Excuses (8)
 *   §1.A.3c — Attendance (12)
 *   §1.A.3d — EOS (10)
 *   §1.A.3e — Travel (3 new — 5 already in catalog.ts seed)
 *   §1.A.3f — Comp (10)
 *   §1.A.3g — Benefits (8)
 *   §1.A.3h — Employee (8)
 *   §1.A.3i — Org (6)
 *   §1.B    — Analytics (18)
 *   §1.C    — Compliance (14)
 *   §1.D    — AI/Agent (10)
 *
 * Total: 152 reports added here + 9 in catalog.ts seed = 161
 * (over the 157 target — clean: travel reports are 5 implemented
 * + 3 new = 8 per §1.A.3e instead of the original 5 stubs).
 */

import type { ReportManifest } from "./catalog";

// ---------------------------------------------------------------------------
// §1.A.1 — Leaves (15)
// ---------------------------------------------------------------------------

export const LEAVES_REPORTS: ReportManifest[] = [
  m("balance_summary", "leaves", "tashghil", "haiku", "operational"),
  m("consumption_analysis", "leaves", "tashghil", "haiku", "operational"),
  m("forecast_year_end", "leaves", "tashghil", "haiku", "trend"),
  m("cost_analysis", "leaves", "rawatib", "haiku", "cost"),
  m("trend_12_month", "leaves", "tashghil", "haiku", "trend"),
  m("carry_forward_risk", "leaves", "tashghil", "haiku", "operational"),
  m("encashment_cycle_history", "leaves", "rawatib", "haiku", "operational"),
  m("maternity_pipeline", "leaves", "mutabaa", "haiku", "compliance", "151"),
  m("sick_leave_patterns", "leaves", "tashghil", "haiku", "trend", "117"),
  m("hajj_leave_tracking", "leaves", "tashghil", "haiku", "operational"),
  m("adjustment_audit_trail", "leaves", "mutabaa", "haiku", "compliance"),
  m("approval_sla", "leaves", "tashghil", "haiku", "operational"),
  m("by_manager", "leaves", "tashghil", "haiku", "analytics"),
  m("coverage_gap_analysis", "leaves", "tashghil", "haiku", "operational"),
  m("article_109_floor_audit", "leaves", "mutabaa", "template", "compliance", "109"),
];

// ---------------------------------------------------------------------------
// §1.A.2 — Payroll (20)
// ---------------------------------------------------------------------------

export const PAYROLL_REPORTS: ReportManifest[] = [
  m("summary_by_period", "payroll", "rawatib", "haiku", "operational"),
  m("cost_breakdown", "payroll", "rawatib", "haiku", "cost"),
  m("ytd_statement", "payroll", "rawatib", "haiku", "operational"),
  m("earnings_by_category", "payroll", "rawatib", "haiku", "cost"),
  m("deductions_by_category", "payroll", "rawatib", "haiku", "cost"),
  m("gosi_contribution", "payroll", "mutabaa", "haiku", "compliance"),
  m("bank_file_audit", "payroll", "rawatib", "haiku", "operational"),
  m("wps_status", "payroll", "rawatib", "haiku", "compliance"),
  m("gl_reconciliation", "payroll", "rawatib", "haiku", "operational"),
  m("variance", "payroll", "rawatib", "haiku", "trend"),
  m("off_cycle_log", "payroll", "rawatib", "haiku", "operational"),
  m("payslip_distribution_status", "payroll", "rawatib", "haiku", "operational"),
  m("overtime_cost_analysis", "payroll", "rawatib", "haiku", "cost", "107"),
  m("loan_deduction_schedule", "payroll", "rawatib", "haiku", "operational"),
  m("allowance_distribution", "payroll", "rawatib", "haiku", "cost"),
  m("period_lock_audit", "payroll", "mutabaa", "haiku", "compliance"),
  m("tax_withholding", "payroll", "rawatib", "haiku", "compliance"),
  m("per_employee_history", "payroll", "rawatib", "haiku", "operational"),
  m("department_distribution", "payroll", "rawatib", "haiku", "analytics"),
  m("termination_final_settlement_audit", "payroll", "mutabaa", "haiku", "compliance"),
];

// ---------------------------------------------------------------------------
// §1.A.3a — Loans (10)
// ---------------------------------------------------------------------------

export const LOANS_REPORTS: ReportManifest[] = [
  m("portfolio_outstanding", "loans", "rawatib", "haiku", "cost"),
  m("disbursement_queue", "loans", "tashghil", "haiku", "operational"),
  m("arrears", "loans", "mutabaa", "haiku", "compliance"),
  m("article_90_audit", "loans", "mutabaa", "template", "compliance", "90"),
  m("settlement_summary", "loans", "rawatib", "haiku", "operational"),
  m("product_utilization", "loans", "tashghil", "haiku", "analytics"),
  m("aging", "loans", "rawatib", "haiku", "cost"),
  m("repayment_forecast", "loans", "rawatib", "haiku", "trend"),
  m("default_rate", "loans", "mutabaa", "haiku", "trend"),
  m("top_borrowers", "loans", "rawatib", "haiku", "analytics"),
];

// ---------------------------------------------------------------------------
// §1.A.3b — Excuses (8)
// ---------------------------------------------------------------------------

export const EXCUSES_REPORTS: ReportManifest[] = [
  m("per_employee_history", "excuses", "tashghil", "haiku", "operational"),
  m("quota_utilization", "excuses", "tashghil", "haiku", "operational"),
  m("approval_rate_by_manager", "excuses", "tashghil", "haiku", "analytics"),
  m("common_reasons", "excuses", "tashghil", "haiku", "analytics"),
  m("pattern_detection", "excuses", "mutabaa", "haiku", "trend"),
  m("quota_breach_alerts", "excuses", "mutabaa", "haiku", "compliance"),
  m("type_breakdown", "excuses", "tashghil", "haiku", "operational"),
  m("by_department", "excuses", "tashghil", "haiku", "analytics"),
];

// ---------------------------------------------------------------------------
// §1.A.3c — Attendance (12)
// ---------------------------------------------------------------------------

export const ATTENDANCE_REPORTS: ReportManifest[] = [
  m("late_arrivals", "attendance", "tashghil", "haiku", "operational"),
  m("early_departures", "attendance", "tashghil", "haiku", "operational"),
  m("overtime_hours", "attendance", "tashghil", "haiku", "cost", "107"),
  m("wfh_days", "attendance", "tashghil", "haiku", "operational"),
  m("punch_compliance", "attendance", "mutabaa", "haiku", "compliance"),
  m("shift_adherence", "attendance", "tashghil", "haiku", "operational"),
  m("geofence_violations", "attendance", "mutabaa", "haiku", "compliance"),
  m("biometric_sync_health", "attendance", "tashghil", "haiku", "operational"),
  m("absenteeism", "attendance", "tashghil", "haiku", "trend"),
  m("by_manager", "attendance", "tashghil", "haiku", "analytics"),
  m("anomaly_summary", "attendance", "mutabaa", "haiku", "compliance"),
  m("article_105_audit", "attendance", "mutabaa", "template", "compliance", "105"),
];

// ---------------------------------------------------------------------------
// §1.A.3d — EOS (10)
// ---------------------------------------------------------------------------

export const EOS_REPORTS: ReportManifest[] = [
  m("active_separations", "eos", "tashghil", "haiku", "operational"),
  m("settlement_exposure", "eos", "rawatib", "haiku", "cost"),
  m("clearance_status", "eos", "tashghil", "haiku", "operational"),
  m("gov_filings_status", "eos", "mutabaa", "haiku", "compliance"),
  m("by_reason_code", "eos", "tashghil", "haiku", "analytics"),
  m("tenure_at_termination", "eos", "tashghil", "haiku", "analytics"),
  m("termination_trend", "eos", "tashghil", "haiku", "trend"),
  m("article_80_absconding_tracker", "eos", "mutabaa", "template", "compliance", "80"),
  m("notice_period_compliance", "eos", "mutabaa", "template", "compliance", "75"),
  m("settlement_payment_timeline", "eos", "rawatib", "haiku", "operational"),
];

// ---------------------------------------------------------------------------
// §1.A.3e — Travel (3 new — 5 already seeded in catalog.ts)
// ---------------------------------------------------------------------------

export const TRAVEL_REPORTS_NEW: ReportManifest[] = [
  m("trip_count_by_destination", "travel", "tashghil", "haiku", "analytics"),
  m("top_travelers", "travel", "tashghil", "haiku", "analytics"),
  m("advance_unrecovered", "travel", "rawatib", "haiku", "cost"),
  m("cost_by_department", "travel", "rawatib", "haiku", "cost"),
];

// ---------------------------------------------------------------------------
// §1.A.3f — Comp (10)
// ---------------------------------------------------------------------------

export const COMP_REPORTS: ReportManifest[] = [
  m("band_utilization", "comp", "rawatib", "haiku", "analytics"),
  m("salary_distribution", "comp", "rawatib", "haiku", "analytics"),
  m("merit_cycle_summary", "comp", "rawatib", "haiku", "operational"),
  m("bonus_payouts", "comp", "rawatib", "haiku", "cost"),
  m("commission_analytics", "comp", "rawatib", "haiku", "cost"),
  m("equity_tracking", "comp", "rawatib", "haiku", "operational"),
  m("compa_ratio", "comp", "rawatib", "haiku", "analytics"),
  m("article_60_consent_log", "comp", "mutabaa", "template", "compliance", "60"),
  m("comp_change_history", "comp", "rawatib", "haiku", "operational"),
  m("pay_equity_analysis", "comp", "rawatib", "haiku", "analytics"),
];

// ---------------------------------------------------------------------------
// §1.A.3g — Benefits (8)
// ---------------------------------------------------------------------------

export const BENEFITS_REPORTS: ReportManifest[] = [
  m("enrollment_summary", "benefits", "tashghil", "haiku", "operational"),
  m("premium_cost", "benefits", "rawatib", "haiku", "cost"),
  m("dependent_coverage", "benefits", "tashghil", "haiku", "operational"),
  m("claims_register", "benefits", "tashghil", "haiku", "operational"),
  m("provider_invoice_reconciliation", "benefits", "rawatib", "haiku", "cost"),
  m("cchi_compliance", "benefits", "mutabaa", "template", "compliance"),
  m("renewal_pricing_trend", "benefits", "rawatib", "haiku", "trend"),
  m("voluntary_retirement_participation", "benefits", "tashghil", "haiku", "operational"),
];

// ---------------------------------------------------------------------------
// §1.A.3h — Employee (8)
// ---------------------------------------------------------------------------

export const EMPLOYEE_REPORTS: ReportManifest[] = [
  m("master_roster", "employee", "tashghil", "haiku", "operational"),
  m("probation_pipeline", "employee", "tashghil", "haiku", "operational", "53"),
  m("iqama_expiry_forecast", "employee", "mutabaa", "haiku", "compliance"),
  m("birthday_anniversary", "employee", "tashghil", "haiku", "operational"),
  m("nationality_distribution", "employee", "tashghil", "haiku", "analytics"),
  m("gender_distribution", "employee", "tashghil", "haiku", "analytics"),
  m("marital_distribution", "employee", "tashghil", "haiku", "analytics"),
  m("age_band_distribution", "employee", "tashghil", "haiku", "analytics"),
];

// ---------------------------------------------------------------------------
// §1.A.3i — Org (6)
// ---------------------------------------------------------------------------

export const ORG_REPORTS: ReportManifest[] = [
  m("headcount_by_org_unit", "org", "tashghil", "haiku", "operational"),
  m("span_of_control", "org", "tashghil", "haiku", "analytics"),
  m("vacancy_report", "org", "tashghil", "haiku", "operational"),
  m("position_fill_rate", "org", "tashghil", "haiku", "trend"),
  m("org_chart_depth", "org", "tashghil", "haiku", "analytics"),
  m("department_cost", "org", "rawatib", "haiku", "cost"),
];

// ---------------------------------------------------------------------------
// §1.B — Cross-domain Analytics (18) — Sonnet narration
// ---------------------------------------------------------------------------

export const ANALYTICS_REPORTS: ReportManifest[] = [
  m("headcount_evolution", "analytics", "tashghil", "sonnet", "trend"),
  m("turnover_analysis", "analytics", "tashghil", "sonnet", "analytics"),
  m("diversity_snapshot", "analytics", "tashghil", "sonnet", "analytics"),
  m("total_reward_cost", "analytics", "rawatib", "sonnet", "cost"),
  m("manager_span_of_control", "analytics", "tashghil", "sonnet", "analytics"),
  m("performance_distribution", "analytics", "tashghil", "sonnet", "analytics"),
  m("workforce_cost_per_department", "analytics", "rawatib", "sonnet", "cost"),
  m("new_hire_quality", "analytics", "tashghil", "sonnet", "trend"),
  m("engagement_proxy", "analytics", "tashghil", "sonnet", "analytics"),
  m("compensation_equity_analysis", "analytics", "rawatib", "sonnet", "analytics"),
  m("promotion_rate_by_grade", "analytics", "tashghil", "sonnet", "analytics"),
  m("internal_mobility", "analytics", "tashghil", "sonnet", "analytics"),
  m("workforce_aging", "analytics", "tashghil", "sonnet", "analytics"),
  m("saudization_rate", "analytics", "mutabaa", "sonnet", "compliance"),
  m("productivity_proxy", "analytics", "tashghil", "sonnet", "analytics"),
  m("org_health_score", "analytics", "tashghil", "sonnet", "analytics"),
  m("onboarding_effectiveness", "analytics", "tashghil", "sonnet", "trend"),
  m("manager_effectiveness", "analytics", "tashghil", "sonnet", "analytics"),
];

// ---------------------------------------------------------------------------
// §1.C — Compliance (14) — template narration
// ---------------------------------------------------------------------------

export const COMPLIANCE_REPORTS: ReportManifest[] = [
  m("article_90_cap", "compliance", "mutabaa", "template", "compliance", "90"),
  m("article_60_consent", "compliance", "mutabaa", "template", "compliance", "60"),
  m("article_53_probation", "compliance", "mutabaa", "template", "compliance", "53"),
  m("article_75_notice_period", "compliance", "mutabaa", "template", "compliance", "75"),
  m("article_80_termination_subtype", "compliance", "mutabaa", "template", "compliance", "80"),
  m("article_84_85_eos", "compliance", "mutabaa", "template", "compliance", "84-85"),
  m("article_105_working_hours", "compliance", "mutabaa", "template", "compliance", "105"),
  m("article_107_ot_premium", "compliance", "mutabaa", "template", "compliance", "107"),
  m("article_109_leave_floor", "compliance", "mutabaa", "template", "compliance", "109"),
  m("article_117_sick_leave_tier", "compliance", "mutabaa", "template", "compliance", "117"),
  m("article_151_152_maternity", "compliance", "mutabaa", "template", "compliance", "151-152"),
  m("cchi_medical_coverage_gap", "compliance", "mutabaa", "template", "compliance"),
  m("gosi_filing_reconciliation", "compliance", "mutabaa", "template", "compliance"),
  m("iqama_expiry_forecast", "compliance", "mutabaa", "template", "compliance"),
];

// ---------------------------------------------------------------------------
// §1.D — AI/Agent (10)
// ---------------------------------------------------------------------------

export const AGENT_REPORTS: ReportManifest[] = [
  m("decision_frequency", "agents", "khidmat", "haiku", "analytics"),
  m("confidence_drift", "agents", "khidmat", "haiku", "trend"),
  m("auto_resolve_rate_by_mode", "agents", "khidmat", "haiku", "analytics"),
  m("escalation_reasons", "agents", "khidmat", "haiku", "analytics"),
  m("cost_per_decision", "agents", "khidmat", "haiku", "cost"),
  m("mutabaa_veto_frequency", "agents", "mutabaa", "haiku", "compliance"),
  m("ai_brief_engagement", "agents", "khidmat", "haiku", "analytics"),
  m("khidmat_intent_distribution", "agents", "khidmat", "haiku", "analytics"),
  m("agent_reasoning_trace_health", "agents", "khidmat", "haiku", "operational"),
  m("ai_call_volume", "agents", "khidmat", "haiku", "operational"),
];

// ---------------------------------------------------------------------------
// Aggregate
// ---------------------------------------------------------------------------

export const ALL_EXTRA_MANIFESTS: ReportManifest[] = [
  ...LEAVES_REPORTS,
  ...PAYROLL_REPORTS,
  ...LOANS_REPORTS,
  ...EXCUSES_REPORTS,
  ...ATTENDANCE_REPORTS,
  ...EOS_REPORTS,
  ...TRAVEL_REPORTS_NEW,
  ...COMP_REPORTS,
  ...BENEFITS_REPORTS,
  ...EMPLOYEE_REPORTS,
  ...ORG_REPORTS,
  ...ANALYTICS_REPORTS,
  ...COMPLIANCE_REPORTS,
  ...AGENT_REPORTS,
];

// ---------------------------------------------------------------------------
// Helper — terse manifest constructor
// ---------------------------------------------------------------------------

function m(
  id: string,
  domain: ReportManifest["domain"],
  agent: ReportManifest["agent"],
  aiTier: ReportManifest["aiTier"],
  category: ReportManifest["category"],
  article?: string,
): ReportManifest {
  return {
    id,
    domain,
    titleKey: `reports.${domain}_${id}_title`,
    descriptionKey: `reports.${domain}_${id}_desc`,
    agent,
    aiTier,
    category,
    expectedDuration: "fast",
    article,
    shipped: true,
  };
}
